import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { selectActiveLayer, getActiveStep, removeActiveLayer } from '../../../../../../redux/toolsSlice';
import { updateLayer } from '../../../../../../redux/stepsSlice';
import { http_get } from '../../../../utils/api_client';
import { API_ENDPOINTS } from '../../../../utils/endpoints';
import TokenField from './TokenField';

const TYPE_LABEL = {
  large_text: 'Heading', heading: 'Heading', body: 'Text', text: 'Text', button: 'Button',
  image: 'Image', video: 'Video', walkthrough: 'Walkthrough', embedded_code: 'Embed code',
  short_answer: 'Short answer', long_answer: 'Long answer', multiple_choice: 'Multiple choice',
  multi_select: 'Multi-select', checkbox: 'Checkboxes', file_upload: 'File upload',
};

const HEADING_LEVELS = [{ label: 'H1', size: 34 }, { label: 'H2', size: 26 }, { label: 'H3', size: 20 }];
const OPTION_TYPES = ['multiple_choice', 'multi_select', 'checkbox'];
const STANDARD_ATTR = [
  { key: 'first_name', label: 'First name' }, { key: 'last_name', label: 'Last name' },
];
// Attributes set elsewhere (not captured from a form answer) — never a save target.
const BLOCKED_ATTR = ['company', 'role', 'plan_tier', 'segment'];

const AlignIcon = ({ a }) => {
  const d = { left: 'M4 6h16M4 12h10M4 18h13', center: 'M4 6h16M7 12h10M6 18h12', right: 'M4 6h16M10 12h10M7 18h13' }[a];
  return <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d={d} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>;
};

const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="#9AA6AE"><path d="M10 2 9 3H5a1 1 0 0 0 0 2h14a1 1 0 0 0 0-2h-4l-1-1h-4ZM5 7v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7H5Z" /></svg>
);

const Toggle = ({ on, onClick }) => (
  <button type="button" className={`un-pe-toggle ${on ? 'on' : 'off'}`} onClick={onClick} aria-pressed={on}><i /></button>
);

const Seg = ({ value, options, onChange }) => (
  <div className="un-pe-seg2">
    {options.map((o) => (
      <button key={o.value} type="button" className={`un-pe-seg2__s${value === o.value ? ' on' : ''}`} onClick={() => onChange(o.value)}>{o.label}</button>
    ))}
  </div>
);

// Personalization-attribute picker (shared by form inputs). Filters out
// attributes that are set elsewhere and can't be captured from an answer.
const AttrSelect = ({ value, onChange }) => {
  const [attrs, setAttrs] = useState({ standard: STANDARD_ATTR, custom: [] });
  useEffect(() => {
    let alive = true;
    http_get(API_ENDPOINTS.GET_PERSONALIZATION_ATTRIBUTES)
      .then(({ data }) => { if (alive && data) setAttrs({ standard: data.standard || STANDARD_ATTR, custom: data.custom || [] }); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const standard = (attrs.standard || []).filter((a) => !BLOCKED_ATTR.includes(a.key));
  return (
    <select className="un-pe-field" value={value || ''} onChange={(e) => onChange(e.target.value)}>
      <option value="">Choose an attribute…</option>
      {standard.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
      {(attrs.custom || []).map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
    </select>
  );
};

// Walkthrough picker — stores the chosen walkthrough id on layer.source, which
// the editor + learner embed both read.
const WalkthroughPicker = ({ layer, set }) => {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    let alive = true;
    http_get(API_ENDPOINTS.SEARCH_WALKTHROUGH, { by: 'aasm_state', keyword: 'published' })
      .then(({ data }) => { if (alive && Array.isArray(data)) setOptions(data.map((w) => ({ value: String(w.id), label: w.name }))); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return (
    <>
      <p className="un-pe-lab">Walkthrough</p>
      <select className="un-pe-field" value={layer.source || ''} onChange={(e) => set({ source: e.target.value })}>
        <option value="">Select a walkthrough…</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </>
  );
};

// Per-element inspector controls, wired to the real layer via updateLayer.
const ElementControls = () => {
  const dispatch = useDispatch();
  const activeLayer = useSelector((state) => selectActiveLayer(state));
  const activeStep = useSelector((state) => getActiveStep(state));
  const [themeOpen, setThemeOpen] = useState(false);

  if (!activeLayer || !activeLayer.id) return null;

  const type = activeLayer.type;
  const set = (patch) => dispatch(updateLayer({ stepId: activeStep.id, layer: { ...activeLayer, ...patch } }));

  const AlignmentRow = (
    <>
      <p className="un-pe-lab">Alignment</p>
      <div className="un-pe-align">
        {['left', 'center', 'right'].map((a) => (
          <button key={a} type="button" className={(activeLayer.textAlign || 'left') === a ? 'on' : ''} onClick={() => set({ textAlign: a })} aria-label={a}><AlignIcon a={a} /></button>
        ))}
      </div>
    </>
  );

  const ColorRow = (
    <>
      <p className="un-pe-lab">Color</p>
      <label className="un-pe-colorrow">
        <input type="color" value={activeLayer.fill || '#14202B'} onChange={(e) => set({ fill: e.target.value })} />
        Text color<span className="hex">{activeLayer.fill || '#14202B'}</span>
      </label>
    </>
  );

  const ThemeAccordion = (
    <div className={`un-pe-accord${themeOpen ? ' is-open' : ''}`}>
      <button type="button" className="un-pe-accord__h" onClick={() => setThemeOpen((v) => !v)}>
        Theme — colors &amp; fonts
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M6.53 13.83 10.35 10 6.53 6.18 7.7 5l5 5-5 5-1.17-1.17Z" fill="#9AA6AE" /></svg>
      </button>
      {themeOpen && (
        <div className="un-pe-accord__b">
          <p className="un-pe-subtle" style={{ marginTop: 0 }}>Theme colors and fonts are set at the path level and apply across every step.</p>
        </div>
      )}
    </div>
  );

  let body;
  if (type === 'large_text' || type === 'heading') {
    body = (
      <>
        <p className="un-pe-lab">Content</p>
        <TokenField multiline value={activeLayer.content} placeholder="Type here…" onChange={(v) => set({ content: v })} />
        <p className="un-pe-lab">Level</p>
        <div className="un-pe-seg2">
          {HEADING_LEVELS.map((l) => (
            <button key={l.label} type="button" className={`un-pe-seg2__s${(activeLayer.fontSize || 34) === l.size ? ' on' : ''}`} onClick={() => set({ fontSize: l.size })}>{l.label}</button>
          ))}
        </div>
        {AlignmentRow}{ColorRow}{ThemeAccordion}
      </>
    );
  } else if (type === 'body' || type === 'text') {
    body = (
      <>
        <p className="un-pe-subtle" style={{ marginTop: 0 }}>
          Select the text on the canvas to format it. The floating toolbar has <b>bold</b>, italic, underline,
          font, size, alignment, lists and links — like a document.
        </p>
        {ThemeAccordion}
      </>
    );
  } else if (type === 'button') {
    const action = activeLayer.action || 'link';
    const destMeta = {
      link: { label: 'Destination', ph: 'https://…' },
      step: { label: 'Go to step', ph: 'Step number, e.g. 3' },
      walkthrough: { label: 'Walkthrough id', ph: 'Walkthrough id' },
    }[action];
    body = (
      <>
        <p className="un-pe-lab">Label</p>
        <TokenField value={activeLayer.content} placeholder="Button label" onChange={(v) => set({ content: v })} />
        <p className="un-pe-lab">When clicked</p>
        <select className="un-pe-field" value={action} onChange={(e) => set({ action: e.target.value })}>
          <option value="link">Open a link</option>
          <option value="step">Go to a step</option>
          <option value="walkthrough">Launch a walkthrough</option>
          <option value="complete">Mark step complete</option>
        </select>
        {destMeta && (
          <>
            <p className="un-pe-lab">{destMeta.label}</p>
            <input className="un-pe-field" value={activeLayer.destination || ''} placeholder={destMeta.ph} onChange={(e) => set({ destination: e.target.value })} />
          </>
        )}
        <p className="un-pe-lab">Style</p>
        <Seg value={activeLayer.style || 'primary'} options={[{ value: 'primary', label: 'Primary' }, { value: 'secondary', label: 'Secondary' }, { value: 'link', label: 'Link' }]} onChange={(v) => set({ style: v })} />
        {AlignmentRow}{ThemeAccordion}
      </>
    );
  } else if (type === 'video') {
    body = (
      <>
        <p className="un-pe-lab">Source <span className="un-pe-opt">· Vimeo, YouTube, Wistia</span></p>
        <input className="un-pe-field" value={activeLayer.source || ''} placeholder="https://vimeo.com/…" onChange={(e) => set({ source: e.target.value })} />
        <p className="un-pe-lab">Aspect ratio</p>
        <Seg value={activeLayer.aspect || '16:9'} options={[{ value: '16:9', label: '16:9' }, { value: '4:3', label: '4:3' }, { value: 'square', label: 'Square' }]} onChange={(v) => set({ aspect: v })} />
        {ThemeAccordion}
      </>
    );
  } else if (type === 'image') {
    body = (
      <>
        <p className="un-pe-lab">Source</p>
        <input className="un-pe-field" value={activeLayer.source || ''} placeholder="https://… or upload on the canvas" onChange={(e) => set({ source: e.target.value })} />
        <p className="un-pe-lab">Alt text <span className="un-pe-opt">· accessibility</span></p>
        <input className="un-pe-field" value={activeLayer.alt || ''} placeholder="Describe the image" onChange={(e) => set({ alt: e.target.value })} />
        <p className="un-pe-lab">Width</p>
        <Seg value={activeLayer.width || 'full'} options={[{ value: 'full', label: 'Full' }, { value: 'inset', label: 'Inset' }, { value: 'small', label: 'Small' }]} onChange={(v) => set({ width: v })} />
        {ThemeAccordion}
      </>
    );
  } else if (type === 'embedded_code') {
    body = (
      <>
        <p className="un-pe-lab">Code</p>
        <textarea className="un-pe-field" style={{ minHeight: 96, fontFamily: 'ui-monospace, Menlo, monospace' }} value={activeLayer.source || ''} placeholder="<iframe src=…></iframe>" onChange={(e) => set({ source: e.target.value })} />
        <p className="un-pe-lab">Height</p>
        <input className="un-pe-field" value={activeLayer.height || ''} placeholder="420 px" onChange={(e) => set({ height: e.target.value })} />
        <p className="un-pe-lab">Alignment</p>
        <Seg value={activeLayer.embedAlign || 'full'} options={[{ value: 'full', label: 'Full' }, { value: 'inset', label: 'Inset' }]} onChange={(v) => set({ embedAlign: v })} />
        <p className="un-pe-subtle">Embeds render live in the learner view. We sanitize pasted markup for safety.</p>
      </>
    );
  } else if (type === 'walkthrough') {
    const launch = activeLayer.launchStyle || 'inline';
    body = (
      <>
        <WalkthroughPicker layer={activeLayer} set={set} />
        <p className="un-pe-lab">Launch style</p>
        <Seg value={launch} options={[{ value: 'inline', label: 'Inline' }, { value: 'modal', label: 'Modal' }, { value: 'button', label: 'Button' }]} onChange={(v) => set({ launchStyle: v })} />
        {launch === 'button' && (
          <>
            <p className="un-pe-lab">Button label <span className="un-pe-opt">· if button</span></p>
            <input className="un-pe-field" value={activeLayer.buttonLabel || ''} placeholder="Start the walkthrough" onChange={(e) => set({ buttonLabel: e.target.value })} />
          </>
        )}
        <div className="un-pe-torow"><span className="un-pe-torow__l">Required to complete step</span><Toggle on={Boolean(activeLayer.required)} onClick={() => set({ required: !activeLayer.required })} /></div>
        {ThemeAccordion}
      </>
    );
  } else if (type === 'short_answer' || type === 'long_answer') {
    body = (
      <>
        <p className="un-pe-lab">Question</p>
        <TokenField value={activeLayer.question} placeholder="Ask a question…" onChange={(v) => set({ question: v })} />
        <p className="un-pe-lab">Placeholder</p>
        <input className="un-pe-field" value={activeLayer.answer || ''} placeholder="Their answer…" onChange={(e) => set({ answer: e.target.value })} />
        <div className="un-pe-torow" style={{ marginTop: 14 }}><span className="un-pe-torow__l">Required</span><Toggle on={Boolean(activeLayer.required)} onClick={() => set({ required: !activeLayer.required })} /></div>
        <p className="un-pe-lab">Save answer to attribute <span className="un-pe-opt">· optional</span></p>
        <AttrSelect value={activeLayer.saveToAttribute} onChange={(v) => set({ saveToAttribute: v })} />
        <p className="un-pe-subtle">Mapping the answer to an attribute lets later steps personalize with it — e.g. save “role”, then greet by role.</p>
      </>
    );
  } else if (OPTION_TYPES.includes(type)) {
    const mc = (Array.isArray(activeLayer.multipleChoice) && activeLayer.multipleChoice[0]) || { question: '', options: [] };
    const setMC = (patch) => set({ multipleChoice: [{ ...mc, ...patch }] });
    const opts = mc.options || [];
    const setOpt = (idx, value) => setMC({ options: opts.map((o, i) => (i === idx ? { ...o, value } : o)) });
    const delOpt = (idx) => setMC({ options: opts.filter((_, i) => i !== idx) });
    const addOpt = () => setMC({ options: [...opts, { id: uuidv4(), value: `Option ${opts.length + 1}`, isChecked: false }] });
    body = (
      <>
        <p className="un-pe-lab">Question</p>
        <TokenField value={mc.question} placeholder="Ask a question…" onChange={(v) => setMC({ question: v })} />
        <p className="un-pe-lab">Options</p>
        {opts.map((o, idx) => (
          <div key={o.id || idx} className="un-pe-optrow">
            <input className="un-pe-field" value={o.value || ''} onChange={(e) => setOpt(idx, e.target.value)} />
            <button type="button" className="un-pe-optrow__del" aria-label="Remove option" onClick={() => delOpt(idx)}><TrashIcon /></button>
          </div>
        ))}
        <button type="button" className="un-pe-addopt" onClick={addOpt}>＋ Add option</button>
        <div className="un-pe-torow" style={{ marginTop: 14 }}><span className="un-pe-torow__l">Required</span><Toggle on={Boolean(activeLayer.required)} onClick={() => set({ required: !activeLayer.required })} /></div>
        <p className="un-pe-lab">Save to attribute <span className="un-pe-opt">· optional</span></p>
        <AttrSelect value={activeLayer.saveToAttribute} onChange={(v) => set({ saveToAttribute: v })} />
      </>
    );
  } else if (type === 'file_upload') {
    body = (
      <>
        <p className="un-pe-lab">Label</p>
        <input className="un-pe-field" value={activeLayer.label || ''} placeholder="Upload label" onChange={(e) => set({ label: e.target.value })} />
        <p className="un-pe-lab">Allowed types</p>
        <input className="un-pe-field" value={activeLayer.allowedTypes || ''} placeholder="PDF, PNG, JPG" onChange={(e) => set({ allowedTypes: e.target.value })} />
        <p className="un-pe-lab">Max size</p>
        <input className="un-pe-field" value={activeLayer.maxSize || ''} placeholder="10 MB" onChange={(e) => set({ maxSize: e.target.value })} />
        <div className="un-pe-torow" style={{ marginTop: 14 }}><span className="un-pe-torow__l">Required</span><Toggle on={Boolean(activeLayer.required)} onClick={() => set({ required: !activeLayer.required })} /></div>
        <p className="un-pe-subtle">Uploaded files appear in the learner&apos;s profile under <b>Files</b>.</p>
      </>
    );
  } else {
    body = <p className="un-pe-subtle" style={{ marginTop: 0 }}>Edit this element directly on the canvas.</p>;
  }

  return (
    <div className="un-pe-insp">
      <div className="un-pe-isel">
        <button type="button" className="un-pe-isel__back" onClick={() => dispatch(removeActiveLayer())}>&lsaquo; Step</button>
        <span className="un-pe-isel__cur">{TYPE_LABEL[type] || 'Element'}</span>
      </div>
      {body}
    </div>
  );
};

export default ElementControls;
